import { __, type FormCheckOptions } from "@/shared/ui";
import type {
	LoanGoal,
	LoanGoalOption,
	MortgageSearchParams,
	PeriodCategory,
	TargetPropertyAcceptableType,
} from "./types";

export const COL_PROPS = {
	xs: 12,
	md: 6,
	xl: 3,
	lg: 3,
};

export const TYPE_OF_PROPERTY_FOR_OTTHON_START: FormCheckOptions<TargetPropertyAcceptableType> = [
	{
		label: __("Lakás"),
		value: "LAKAS",
	},
	{
		label: __("Ház"),
		value: "LAKOHAZ",
	},
];

export const INTEREST_FILTER_OPTIONS: FormCheckOptions<PeriodCategory> = [
	{
		label: `15-30 ${__("év")}`,
		value: "FIFTEEN_YEARS,TWENTY_YEARS,TWENTY_FIVE_YEARS,THIRTY_YEARS",
		infoTooltip: "A jövőbeni kamatemelkedés KIS MÉRTÉKBEN MEGNÖVELHETI a hitel havi törlesztőrészletét.",
	},
	{
		label: `10 ${__("év")}`,
		value: "TEN_YEARS",
		infoTooltip: "A jövőbeni kamatemelkedés MÉRSÉKELTEN MEGNÖVELHETI a hitel havi törlesztőrészletét.",
	},
	{
		label: __("Végig fix*"),
		value: "FULL",
		infoTooltip: "A jövőbeni kamatemelkedés NEM NÖVELHETI a hitel havi törlesztőrészletét.",
	},
	{
		label: `5 ${__("év")}`,
		value: "FIVE_YEARS",
		infoTooltip: "A jövőbeni kamatemelkedés MEGNÖVELHETI a hitel havi törlesztőrészletét.",
	},
	{
		label: __("Változó kamat"),
		value: "THREE_MONTHS,SIX_MONTHS,ONE_YEAR,THREE_YEARS",
		infoTooltip: "A jövőbeni kamatemelkedés SZÁMOTTEVŐ MÉRTÉKBEN MEGNÖVELHETI a hitel havi törlesztőrészletét.",
	},
];

export const GROUP_LIMIT = 3;
export const LOAN_GOALS: LoanGoalOption[] = [
	{ value: "HASZNALT_LAKAS", label: __("Használt lakás vásárlás") },
	{ value: "UJ_LAKAS", label: __("Új lakás vásárlás") },
	{ value: "SZAB_FEL", label: __("Szabad felhasználás") },
	{ value: "HITELKIVALTAS", label: __("Hitelkiváltás (lakáscélú)") },
	{ value: "EPITES", label: __("Építés") },
	{ value: "FELUJITAS", label: __("Felújítás") },
	{ value: "KORSZERUSITES", label: __("Korszerűsítés") },
	{ value: "BOVITES", label: __("Bővítés") },
];

export const CSOK_LOAN_GOALS: LoanGoal[] = ["EPITES", "HASZNALT_LAKAS", "UJ_LAKAS"];
export const CSOK_PLUS_LOAN_GOALS: LoanGoal[] = ["BOVITES", "EPITES", "HASZNALT_LAKAS", "UJ_LAKAS"];
export const SOCIAL_DISCOUNT_LOAN_GOALS: LoanGoal[] = ["BOVITES", ...CSOK_LOAN_GOALS];
export const GREEN_LOAN_GOALS: LoanGoal[] = ["UJ_LAKAS", "EPITES"];

export const CONFIG = {
	MIN_LOAN_AMOUNT: 2_500_000,
	MAX_LOAN_AMOUNT: 500_000_000,
	MAX_OFK_LOAN_AMOUNT: 6_000_000,
	MAX_CSOK_LOAN_AMOUNT_FOR_ONE_OR_TWO_CHILDREN: 10_000_000,
	MAX_CSOK_LOAN_AMOUNT: 15_000_000,
	MAX_CSOK_PLUSZ_LOAN_AMOUNT_FOR_ONE_CHILDREN: 15_000_000,
	MAX_CSOK_PLUSZ_LOAN_AMOUNT_FOR_TWO_CHILDREN: 30_000_000,
	MAX_CSOK_PLUSZ_LOAN_AMOUNT_FOR_THREE_CHILDREN: 50_000_000,

	// Konstans értékek - Otthon Start
	MAX_OTTHON_START_PROPERTY_VALUE_FOR_FLAT: 100_000_000,
	MAX_OTTHON_START_PROPERTY_VALUE_FOR_HOUSE: 150_000_000,
	MAX_OTTHON_START_LOAN_AMOUNT: 50_000_000,

	// Konstans értékek - Futamidő
	MIN_MATURITY: 1,
	MAX_MATURITY: 35,
	MAX_CSOK_MATURITY: 25,
	MAX_OFK_MATURITY: 10,

	// Konstans értékek - Ingatlan érték
	MIN_REAL_ESTATE_VALUE: 2_500_000 * 1.25,
	MAX_REAL_ESTATE_VALUE: 500_000_000 * 1.25,

	MAX_CSOK_PLUS_AND_FIRST_FLAT_VALUE: 100_000_000,
	MAX_CSOK_PLUS_AND_FIRST_HOUSE_VALUE: 150_000_000,
	MAX_CSOK_PLUS_AND_NOT_FIRST_PROPERTY_VALUE: 150_000_000,

	// Konstans értékek - Maximális jövedelem
	INCOME_MAX: 10_000_000,

	// Konstans értékek - Gyermekek száma
	MAX_CHILDREN: 10,

	// Konstans értékek - Meglévő hiteltörlesztések
	MAX_CURRENT_INSTALLMENT: 1_000_000,

	// Konstans értékek - Meglévő hitelkeret
	MAX_CURRENT_CREDIT_LINE: 10_000_000,

	// Konstans értékek - Havi megtakarítás
	MAX_MONTHLY_DEPOSIT: 1_000_000,
};

export const DEFAULT_PARAMS: MortgageSearchParams = {
	loanGoal: "HASZNALT_LAKAS",
	loanAmount: 10_000_000,
	maturity: 20,
	income: undefined,
	onePerBank: true,
	onlyGreen: false,
	interestPeriods: [
		"THREE_MONTHS",
		"SIX_MONTHS",
		"ONE_YEAR",
		"THREE_YEARS",
		"FIVE_YEARS",
		"TEN_YEARS",
		"FIFTEEN_YEARS",
		"TWENTY_YEARS",
		"TWENTY_FIVE_YEARS",
		"THIRTY_YEARS",
		"FULL",
	],
	realEstateValue: undefined,
	realEstateLocation: "",
	age: 29,
	currentInstallment: 0,
	currentCreditLine: 0,
	monthlyDeposit: 0,

	receiptOfIncome: true,
	definedInsuranceUsage: true,
	activeAccountPackageUsage: true,
	loanCoverageInsurance: false,
	activeCreditCardUsage: true,
	definedAccountPackageUsage: false,

	numberOfChildrenCommitment: 0,
	numberOfChildrenCurrent: 0,
	wantsFirstHouse: false,
	eligibleForFalusiCsok: true,
	eligibleForCsokPlus: true,
	onlyCsokPlus: false,

	socialDiscount: false,
	csok: false,
	onlyFbl: false,
	onlyOfk: false,
	publicSectorToo: false,
	qualifiesForHigherLtv: false,
	qualifiesForVoluntaryInterestRateCap: false,
	bankCodes: [],
	withEnergyEfficient: false,
	eligibleForHomeStart: false,
	onlyHomeStart: false,
	targetPropertyAcceptableType: "LAKAS",
};

export const DEFAULT_PARAMS_FOR_HOUSE_LOAN: MortgageSearchParams = {
	...DEFAULT_PARAMS,
	loanAmount: 20_000_000,
	maturity: 20,
	income: 450_000,
	realEstateValue: 40_000_000,
	interestPeriods: [
		"FIVE_YEARS",
		"TEN_YEARS",
		"FIFTEEN_YEARS",
		"TWENTY_YEARS",
		"TWENTY_FIVE_YEARS",
		"THIRTY_YEARS",
		"FULL",
	],
};

export const DEFAULT_PARAMS_FOR_BUILD: MortgageSearchParams = {
	...DEFAULT_PARAMS,
	loanGoal: "EPITES",
};

export const DEFAULT_PARAMS_FOR_RENOVATION: MortgageSearchParams = {
	...DEFAULT_PARAMS,
	loanGoal: "FELUJITAS",
};

export const DEFAULT_PARAMS_FOR_SUPPORTED_RENOVATION: MortgageSearchParams = {
	...DEFAULT_PARAMS,
	loanAmount: 6_000_000,
	maturity: 10,
	loanGoal: "FELUJITAS",
	onlyOfk: true,
	numberOfChildrenCommitment: undefined,
	numberOfChildrenCurrent: undefined,
};

export const DEFAULT_PARAMS_FOR_FREE_USE: MortgageSearchParams = {
	...DEFAULT_PARAMS,
	loanGoal: "SZAB_FEL",
};

export const DEFAULT_PARAMS_FOR_LOAN_REDEMPTION: MortgageSearchParams = {
	...DEFAULT_PARAMS,
	loanGoal: "HITELKIVALTAS",
};

// GREEN_LOAN
export const DEFAULT_PARAMS_FOR_GREEN_LOAN: MortgageSearchParams = {
	...DEFAULT_PARAMS,
	loanGoal: "UJ_LAKAS",
	onlyGreen: false,
};

// CSOK_PLUSZ
export const DEFAULT_PARAMS_FOR_CSOK_PLUSZ: MortgageSearchParams = {
	...DEFAULT_PARAMS,
	loanGoal: "HASZNALT_LAKAS",
	onlyGreen: false,
	eligibleForCsokPlus: true,
	onlyCsokPlus: true,
	numberOfChildrenCommitment: undefined,
	numberOfChildrenCurrent: undefined,
	income: undefined,
	realEstateValue: undefined,
};

// CSOK_PLUSZ
export const DEFAULT_PARAMS_FOR_CSOK: MortgageSearchParams = {
	...DEFAULT_PARAMS,
	numberOfChildrenCommitment: undefined,
	numberOfChildrenCurrent: undefined,
	income: undefined,
	realEstateValue: undefined,
};

export const BASIC_SEARCH_FIELDS = ["loanAmount", "maturity", "income", "realEstateValue"];
